/** LEGAL-01 runtime guard — detection of unfilled legal placeholders (no React, pure). */

/**
 * Placeholder-Konvention (wortgleich gespiegelt in scripts/check-legal-placeholders.mjs).
 *
 * Ein Legal-Placeholder ist ein Bracket-Token, das ENTWEDER
 *   1. eines der Literale ist, die scripts/fill-legal-placeholders.mjs ersetzt
 *      (KNOWN_PLACEHOLDER_TOKENS — heutiger Stand), ODER
 *   2. ein reserviertes Präfix trägt: [DEIN…], [DEINE…], [TODO…], [PLATZHALTER…], ODER
 *   3. mehrteilig ist: Großbuchstaben/Ziffern mit mindestens einem Trenner
 *      (Leerzeichen, Bindestrich, Unterstrich, Slash, Punkt) — z. B. [UST-ID],
 *      [HRB-NUMMER], [IBAN-NR], [TEL 0123], [STRAẞE HAUSNUMMER].
 *
 * Bewusst NICHT "jedes ALL-CAPS-Token ab 3 Zeichen" (WR-04): deutsche Rechtstexte
 * enthalten Zitate und Abkürzungen in Klammern — [USA], [DSGVO], [AGB], [BGH] —
 * und die als Placeholder zu werten, ersetzt zur Laufzeit die komplette
 * Impressum-/AGB-Seite durch den Wartungshinweis bzw. bricht den Production-Build.
 * Eine redaktionelle Klammer würde damit eine Rechtsseite unerreichbar machen —
 * selbst ein Compliance-Vorfall.
 *
 * Preis dieser Abwägung: ein EINWORTIGES Placeholder-Token wie [STRAẞE] ist von
 * einem Zitat strukturell nicht unterscheidbar und daher konventionswidrig. Jeder
 * neue Placeholder MUSS mehrteilig sein oder ein reserviertes Präfix tragen —
 * beides deckt der Guard ab. Regel 1 hält zusätzlich die heutigen Literale fest,
 * damit [ADRESSE] (einwortig, historisch) nicht durchrutscht.
 *
 * Alle Muster bewusst OHNE /g-Flag: eine globale Regex trägt `lastIndex` über
 * `.test()`-Aufrufe hinweg und liefert für dieselbe Eingabe abwechselnd true/false.
 */

/** Großbuchstaben inkl. deutscher Umlaute und Versal-ß. */
const UPPER = "A-ZÄÖÜẞ";
/** Ein Wortteil innerhalb der Klammern. */
const PART = `[${UPPER}0-9]+`;
/** Erlaubte Trenner zwischen Wortteilen. */
const SEP = "[ \\-_./]";

/**
 * Die Literale aus dem `rules`-Array in scripts/fill-legal-placeholders.mjs.
 * apps/web/test/legal-guard.test.ts hält diese Liste gegen das Skript synchron.
 */
export const KNOWN_PLACEHOLDER_TOKENS = [
  "[DEIN VORNAME NACHNAME]",
  "[STRASSE HAUSNUMMER]",
  "[PLZ ORT]",
  "[DEINE NUMMER]",
  "[ADRESSE]",
] as const;

/** Escaped ein Literal für die Verwendung in einer Regex-Alternation. */
function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Regel 2: reserviertes Präfix. "DEIN" deckt "DEINE" mit ab (Präfix von Präfix). */
const RESERVED_PREFIX_SOURCE = `\\[(?:DEIN|TODO|PLATZHALTER)(?:${SEP}|[${UPPER}0-9])*\\]`;

/** Regel 3: mehrteiliges Großbuchstaben-Token. */
const MULTIPART_SOURCE = `\\[${PART}(?:${SEP}+${PART})+\\]`;

/** Regel 1: heutige Literale (fängt einwortige Alt-Token wie [ADRESSE]). */
const KNOWN_TOKENS_SOURCE = KNOWN_PLACEHOLDER_TOKENS.map(escapeRegExp).join("|");

/**
 * Vollständige Quelle des Placeholder-Musters. Exportiert, damit der Test sie
 * zeichengleich gegen scripts/check-legal-placeholders.mjs prüfen kann — die
 * beiden Guards dürfen niemals auseinanderlaufen.
 */
export const LEGAL_PLACEHOLDER_PATTERN_SOURCE = `${RESERVED_PREFIX_SOURCE}|${MULTIPART_SOURCE}|${KNOWN_TOKENS_SOURCE}`;

/** Stateless (kein /g) — sicher für wiederholte .test()-Aufrufe. */
export const LEGAL_PLACEHOLDER_PATTERN = new RegExp(LEGAL_PLACEHOLDER_PATTERN_SOURCE);

/** True when the given rendered text still contains an unfilled legal placeholder. */
export function containsLegalPlaceholder(text: string): boolean {
  if (!text) return false;
  return LEGAL_PLACEHOLDER_PATTERN.test(text);
}

/** The matched placeholder token, or null. Placeholder literals are not PII — safe for Sentry triage. */
export function findLegalPlaceholder(text: string): string | null {
  if (!text) return null;
  return text.match(LEGAL_PLACEHOLDER_PATTERN)?.[0] ?? null;
}
